import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { ArenaxWordmark } from './ArenaxWordmark';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, Loader2, ArrowRight } from 'lucide-react';

export default function ArenaxRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error('Name, email and password required'); return; }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await api.post('/arenax/auth/register', form);
      toast.success('Verification code sent — check your email');
      navigate(`/arenax/auth/verify?email=${encodeURIComponent(form.email)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  const onGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/arenax/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <ArenaxLayout>
      <section className="min-h-[80vh] flex items-center justify-center px-5 py-12 arenax-themed">
        <div className="w-full max-w-md" data-testid="arenax-register-page">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <ArenaxWordmark size="xl" />
            </div>
            <p className="text-sm" style={{ color: `hsl(var(--ax-text-muted))` }}>
              Discover venues. Book in seconds. Find your squad.
            </p>
          </div>

          <div
            className="rounded-2xl p-7 border backdrop-blur-sm"
            style={{
              background: 'var(--ax-card-gradient)',
              borderColor: 'var(--ax-border-quiet)',
            }}
          >
            <button
              onClick={onGoogleLogin}
              data-testid="arenax-register-google"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5"
              style={{
                background: 'hsl(var(--ax-bg-elevated))',
                color: `hsl(var(--ax-text))`,
                border: '1px solid var(--ax-input-border)',
              }}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.22-4.74 3.22-8.32z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.87 0-5.29-1.94-6.16-4.53H2.18v2.84A10.99 10.99 0 0012 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 010-4.18V7.07H2.18a10.99 10.99 0 000 9.86l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A10.99 10.99 0 002.18 7.07l3.66 2.84C6.71 7.32 9.13 5.38 12 5.38z"/>
              </svg>
              Continue with Google
            </button>

            <div className="relative my-5 flex items-center">
              <div className="flex-1 h-px" style={{ background: 'var(--ax-divider)' }} />
              <span
                className="px-3 text-[10px] tracking-[0.3em] font-semibold"
                style={{ color: `hsl(var(--ax-text-faint))` }}
              >
                OR EMAIL
              </span>
              <div className="flex-1 h-px" style={{ background: 'var(--ax-divider)' }} />
            </div>

            <form onSubmit={onSubmit} className="space-y-3.5 arenax-operational-form">
              <Field icon={User}  type="text"     label="Name"             testid="arenax-register-name"
                value={form.name}     onChange={v => setForm({ ...form, name: v })}     placeholder="Your name" />
              <Field icon={Mail}  type="email"    label="Email"            testid="arenax-register-email"
                value={form.email}    onChange={v => setForm({ ...form, email: v })}    placeholder="you@example.com" />
              <Field icon={Phone} type="tel"      label="Phone (optional)" testid="arenax-register-phone"
                value={form.phone}    onChange={v => setForm({ ...form, phone: v })}    placeholder="+91 98xxxxxxxx" />
              <Field icon={Lock}  type="password" label="Password"         testid="arenax-register-password"
                value={form.password} onChange={v => setForm({ ...form, password: v })} placeholder="Min 6 characters" />

              <button
                type="submit"
                disabled={loading}
                data-testid="arenax-register-submit"
                className="w-full px-4 py-3 rounded-xl font-bold text-sm shadow-xl flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-60"
                style={{
                  background: 'var(--ax-primary-gradient)',
                  color: `hsl(var(--ax-button-text))`,
                  boxShadow: 'var(--ax-shadow-glow)',
                }}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create Account <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>

            <div className="mt-4 text-center text-xs" style={{ color: `hsl(var(--ax-text-muted))` }}>
              Already a player?{' '}
              <Link
                to="/arenax/auth/login"
                className="font-semibold hover:underline"
                style={{ color: `hsl(var(--ax-accent-cyan))` }}
                data-testid="arenax-register-login-link"
              >
                Sign in
              </Link>
            </div>
            <div className="mt-2 text-center text-[10px]" style={{ color: `hsl(var(--ax-text-faint))` }}>
              By creating an account you agree to Out-Play's{' '}
              <Link to="/terms" className="hover:underline" style={{ color: `hsl(var(--ax-text-muted))` }}>Terms</Link>.
            </div>
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}

function Field({ icon: Icon, type, label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label
        className="text-[11px] tracking-wider uppercase font-bold"
        style={{ color: `hsl(var(--ax-text-muted))` }}
      >
        {label}
      </label>
      <div className="mt-1.5 relative">
        <Icon
          className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: `hsl(var(--ax-text-faint))` }}
        />
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
          className="arenax-input w-full pl-10 pr-4 py-3 rounded-xl text-sm"
        />
      </div>
    </div>
  );
}
